import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useGetSapDealerByCountryDropdownQuery } from '../../api/endpoints/compound';

const pick = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
};

const toIdString = (v) => {
  if (v === undefined || v === null || v === '') return '';
  return String(v);
};

const SapDealerByCountryDropdown = ({
  value,
  onChange,
  className = '',
  disabled = false,
  placeholder = 'Bayi seçiniz veya arayınız',
  countryCode = 'TR',
  itemCount = 50
}) => {
  // UI filter input (görünen)
  const [dropdownFilterValue, setDropdownFilterValue] = useState('');

  // BE'ye giden SearchText (debounce) - sadece listeyi etkiler
  const [searchText, setSearchText] = useState('');
  const debounceRef = useRef(null);

  const handleDropdownFilterInputChange = useCallback((val) => {
    setDropdownFilterValue(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchText((val ?? '').trim());
    }, 350);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // value primitive olsun (id)
  const dropdownValueRaw = value && typeof value === 'object' ? (value.id ?? value.value) : value;
  const dropdownValue = dropdownValueRaw ?? null;
  const dropdownValueStr = toIdString(dropdownValue);

  /**
   * 1) Liste sorgusu: her zaman 50'lik listeyi döndürür (SearchText kullanıcı araması)
   */
  const {
    data: dealerList = [],
    isLoading: isLoadingList
  } = useGetSapDealerByCountryDropdownQuery({
    CountryCode: countryCode,
    ItemCount: itemCount,
    SearchText: searchText || ''
  });

  const listOptions = useMemo(() => {
    return (dealerList || [])
      .map((dealer) => {
        const id = pick(dealer, ['dealerId', 'DealerId', 'id', 'Id']);
        const name = pick(dealer, ['dealerName', 'DealerName', 'name', 'Name']) || '';
        if (id === undefined || id === null) return null;

        return {
          label: String(name || ''),
          value: id
        };
      })
      .filter(Boolean);
  }, [dealerList]);

  const hasSelectedInList = useMemo(() => {
    if (!dropdownValueStr) return true;
    return listOptions.some((o) => toIdString(o.value) === dropdownValueStr);
  }, [listOptions, dropdownValueStr]);

  /**
   * 2) Selected sorgusu: SADECE seçili değer listede yoksa çalışır
   *    - amaç: seçili bayinin label'ını bulup listeye eklemek
   *    - listeyi 1 kayda düşürmez!
   */
  const shouldFetchSelected = !!dropdownValueStr && !hasSelectedInList && !searchText; 
  // not: searchText varken selected fetch etmeyelim; kullanıcı zaten arıyor.

  const {
    data: selectedDealerList = [],
    isLoading: isLoadingSelected
  } = useGetSapDealerByCountryDropdownQuery(
    {
      CountryCode: countryCode,
      ItemCount: 1,                 // sadece seçiliyi yakalamak için yeter
      SearchText: dropdownValueStr   // id ile arama
    },
    { skip: !shouldFetchSelected }
  );

  const selectedOption = useMemo(() => {
    const d = (selectedDealerList || [])[0];
    if (!d) return null;

    const id = pick(d, ['dealerId', 'DealerId', 'id', 'Id']);
    const name = pick(d, ['dealerName', 'DealerName', 'name', 'Name']) || '';
    if (id === undefined || id === null) return null;

    return { label: String(name || ''), value: id };
  }, [selectedDealerList]);

  /**
   * 3) Merge: seçili kayıt listede yoksa (ve selectedOption geldiyse) listenin başına ekle.
   *    selectedOption hiç gelmezse, en azından placeholder göster.
   */
  const mergedOptions = useMemo(() => {
    const base = [...listOptions];

    if (dropdownValueStr && !hasSelectedInList) {
      if (selectedOption && toIdString(selectedOption.value) === dropdownValueStr) {
        base.unshift(selectedOption);
      } else {
        // selectedOption henüz gelmediyse fallback
        base.unshift({ label: `#${dropdownValueStr}`, value: dropdownValue });
      }
    }

    // uniq (value bazlı)
    const uniq = new Map();
    base.forEach((o) => uniq.set(toIdString(o.value), o));
    return Array.from(uniq.values());
  }, [listOptions, dropdownValueStr, hasSelectedInList, selectedOption, dropdownValue]);

  const emptyMessage = (dropdownFilterValue ?? '').trim() ? 'Kayıt bulunamadı' : 'Kayıt yok';

  const filterTemplate = useCallback(
    () => (
      <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
        <InputText
          value={dropdownFilterValue}
          onChange={(e) => handleDropdownFilterInputChange(e.target.value)}
          className="p-dropdown-filter w-full"
          placeholder="Ara"
          autoFocus
          disabled={disabled}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
        <span className="p-dropdown-filter-icon pi pi-search" />
      </div>
    ),
    [dropdownFilterValue, handleDropdownFilterInputChange, disabled]
  );

  const loading = isLoadingList || isLoadingSelected;

  return (
    <Dropdown
      key={countryCode}
      value={dropdownValue}
      onChange={(e) => {
        onChange?.(e.value ?? null);

        // seçim sonrası filtreyi temizle (isteğe göre)
        setDropdownFilterValue('');
        setSearchText('');
      }}
      options={mergedOptions}
      optionLabel="label"
      optionValue="value"
      placeholder={placeholder}
      className={className}
      filter
      filterTemplate={filterTemplate}
      showClear
      loading={loading}
      disabled={disabled}
      appendTo="self"
      emptyMessage={emptyMessage}
      virtualScrollerOptions={{ itemSize: 38 }}
      itemTemplate={(opt) => (
        <div>
          <span style={{ fontWeight: 'bold' }}>{opt.value}</span> {opt.label}
        </div>
      )}
    />
  );
};

export default SapDealerByCountryDropdown;
